import { FC, useCallback, useEffect, useState } from "react";

import { useQueryCache } from "react-query";

import {
  Box,
  PageSetup,
  RestirectedModal,
  SeatsCounter,
  ShimmerPlaceholder,
} from "../../components";
import { TEXTS } from "../../constants";
import { setTeam, useUserDispatch, useUserState } from "../../context";

import { Button, Flex, useBoolean } from "@chakra-ui/react";
import { ANALYTICS_EVENTS } from "constants/analyticEvents";
import { FEATURES, FEATURE_ACTION } from "constants/features";
import { FEATURE_FLAGS } from "constants/flags";
import { useMyTeamsQuery } from "features/auth/queries/useMyTeamsQuery";
import { InviteUserModal } from "features/inviteUser/modal/InviteUserModal";
import { loadPendingEmails } from "features/inviteUser/utils/pendingEmailsStorage";
import ManageSubscription from "features/manageSubscription";
import { OnboardingModal } from "features/onboarding";
import { useCheckPermission, useIsActionAllowed, useTracking } from "hooks";
import { useManageSubscription } from "hooks/useManageSubscription";
import { useUpgradeModal } from "hooks/useUpgradeModal";
import { useUserInvite } from "hooks/useUserInvite";
import { useToasts } from "react-toast-notifications";
import { ITeamResponse } from "types/Team";
import { useFlag } from "utils";
import { getErrorMessage } from "utils/error";
import { isTeamWizardStepComplete } from "utils/userStatus";
import ManageUsersTable from "./ManageUsersTable";
import { useCompleteWizardStep } from "./api/useCompleteWizardStep";
import { useTeamUsersQuery } from "./api/useTeamUsersQuery";

const ManageUsers: FC = () => {
  const [isInviting, setIsInviting] = useState(false);
  const [isUpdatingBGData, setIsUpdatingBGData] = useState(false);
  const track = useTracking();
  const queryCache = useQueryCache();
  const {
    isManageSubscriptionOpen,
    closeManageSubscription,
    customerPortal,
    openManageSubscription,
  } = useManageSubscription();
  const canViewInviteUserModal = useCheckPermission(
    FEATURE_ACTION.VIEW,
    FEATURES.INVITE_USERS_MODAL,
  );
  const canViewManageSubscription = useCheckPermission(
    FEATURE_ACTION.VIEW,
    FEATURES.MANAGE_SUBSCRIPTION,
  );
  const canEditManageSubscription = useCheckPermission(
    FEATURE_ACTION.EDIT,
    FEATURES.MANAGE_SUBSCRIPTION,
  );
  const [
    isRestrictedModalOpen,
    { on: openRestrictedModal, off: closeRestrictedModal },
  ] = useBoolean(false);

  const { handleAction } = useIsActionAllowed();

  const { currentTeam } = useUserState();
  const [isInviteModalOpen, { on: openInviteModal, off: closeInviteModal }] =
    useBoolean(false);
  const { addToast } = useToasts();
  const dispatch = useUserDispatch();
  const isTeamOnboardingVideoEnabled = useFlag(
    FEATURE_FLAGS.TEAM_ONBOARDING_VIDEO_MODAL,
  );

  const { openModal } = useUpgradeModal();

  const teamId = currentTeam?.id as number;
  const {
    num_seats: numSeats = 0,
    pending_seats: pendingSeats = 0,
    assigned_seats: assignedSeats = 0,
  } = currentTeam || {};

  const { mutate: fireCompleteWizardStep } = useCompleteWizardStep();

  const { data: teamsData, getMyTeams } = useMyTeamsQuery({
    onSuccess: (data: ITeamResponse) => {
      const team = data?.teams?.[0];
      dispatch(setTeam(team));
    },
  });
  const { data: teamUsers, refetch: refetchUsers } = useTeamUsersQuery({
    teamId,
  });
  const { mutate: fireSendUserInvite, isLoading: userInviteLoading } =
    useUserInvite();

  const openInviteUserModal = useCallback(() => {
    handleAction(
      canViewInviteUserModal,
      FEATURES.INVITE_USERS_MODAL,
      () => {
        openInviteModal();
        track({
          eventName: ANALYTICS_EVENTS.MANAGE_USERS_OPEN_INVITE_USERS_MODAL,
        });
      },
      openModal,
    );
  }, [canViewInviteUserModal]);

  const handleInviteUsers = (emails: string[], templateId?: number) => {
    setIsInviting(true);
    fireSendUserInvite(
      { teamId, emails, templateId },
      {
        onSuccess: async ({ message }) => {
          queryCache.invalidateQueries([`team-users`, { teamId }]);
          setIsUpdatingBGData(true);
          await Promise.all([refetchUsers(), getMyTeams()]);
          setIsUpdatingBGData(false);

          track({
            eventName: ANALYTICS_EVENTS.MANAGE_USERS_INVITE_USERS,
            customTrackingProps: {
              emails,
            },
          });
          addToast(message, {
            appearance: "success",
          });
          setIsInviting(false);
          closeInviteModal();
        },
        onError: (error: any) => {
          addToast(
            getErrorMessage(error, "Failed to invite users. Please try again."),
            { appearance: "error" },
          );
          setIsInviting(false);
        },
      },
    );
  };

  useEffect(() => {
    const wizard_step = teamsData?.teams?.[0]?.wizard_step;
    if (
      isTeamOnboardingVideoEnabled &&
      wizard_step &&
      !isTeamWizardStepComplete(wizard_step)
    ) {
      setIsOnboardingModalOpen(true);
    }
  }, [isTeamOnboardingVideoEnabled, teamsData]);

  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(false);
  const onboardingOnClose = () => {
    setIsOnboardingModalOpen(false);
    const team_id = teamsData?.teams?.[0]?.id;
    if (team_id) {
      fireCompleteWizardStep({ teamId: team_id });
    }
  };

  const handleIncreaseSeats = () => {
    if (canEditManageSubscription) {
      openManageSubscription();

      track({
        eventName: ANALYTICS_EVENTS.INCREASE_SEATS,
      });
    } else {
      openRestrictedModal();
    }
  };

  useEffect(() => {
    const pendingEmails = loadPendingEmails();
    if (pendingEmails.length > 0 && canViewInviteUserModal) {
      openInviteModal();
      track({
        eventName: ANALYTICS_EVENTS.MANAGE_USERS_OPEN_INVITE_USERS_MODAL,
      });
    }
  }, [canViewInviteUserModal]);

  return (
    <PageSetup
      title={TEXTS.routes.MANAGE_USERS}
      titleElement={
        <Box marginLeft="20px">
          {currentTeam == undefined ? (
            <ShimmerPlaceholder />
          ) : (
            <SeatsCounter
              assignedSeats={assignedSeats}
              pendingSeats={pendingSeats}
              numberOfSeats={numSeats}
            />
          )}
        </Box>
      }
      subTitle={TEXTS.routes.MANAGE_USERS_SUB_TITLE}
      rightBodyElement={
        <Flex gap="2">
          {canViewManageSubscription && (
            <Button
              whiteSpace="nowrap"
              variant="outline"
              onClick={handleIncreaseSeats}
              width="120px"
            >
              {TEXTS.common.INCREASE_SEATS}
            </Button>
          )}
          <Button variant="primary" onClick={openInviteUserModal} width="120px">
            {TEXTS.common.INVITE_USERS}
          </Button>
        </Flex>
      }
      feature={FEATURES.MANAGE_USERS}
    >
      <>
        <ManageUsersTable
          teamId={teamId}
          openAssignSeatsModal={openInviteUserModal}
        />
        {canViewInviteUserModal && (
          <InviteUserModal
            teamUsers={teamUsers?.results}
            isOpen={isInviteModalOpen}
            isLoading={userInviteLoading || isInviting}
            isUpdatingBGData={isUpdatingBGData}
            onClose={closeInviteModal}
            handleInviteUsers={handleInviteUsers}
          />
        )}

        <OnboardingModal
          isOpen={isOnboardingModalOpen}
          onClose={onboardingOnClose}
        />

        <ManageSubscription
          isOpen={isManageSubscriptionOpen}
          onClose={closeManageSubscription}
          customerPortal={customerPortal}
        />
        <RestirectedModal
          isOpen={isRestrictedModalOpen}
          onClose={closeRestrictedModal}
        />
      </>
    </PageSetup>
  );
};

export default ManageUsers;
